import { isWebotServerError } from "./contacts/contactWriteErrors";

/** Any Hebrew letter. See `serverFailureMessage`. */
const HEBREW = /[֐-׿]/;

/**
 * The server's own Hebrew sentence for a failed request, or the caller's fallback — never the
 * transport error.
 *
 * `sendWebotRequest` throws a `WebotServerError` whose `.message` is the raw
 * `Webot POST <url> failed [status=502] [content-type="…"] [body="…"]` envelope, and which parks
 * the server's `error` field on **`.code`** and its `message` field on `.serverMessage`. Most of
 * this app's routes answer `{ error: <Hebrew sentence> }`, so the sentence is in `.code` — as the
 * CRM bulk dialogs found first, and as the WhatsApp surfaces then got wrong everywhere: they read
 * `err?.error`, a property on NEITHER, so ~30 call sites silently discarded every explanation the
 * server sent. Measured 2026-09-23 on the connect screen, the one place that fell through to
 * `.message` instead of a fallback and showed a realtor the raw envelope — with the actual
 * guidance inside it as JSON, truncated at 200 characters.
 *
 * **Hebrew is the test, deliberately.** Customer-facing copy in this product is Hebrew by
 * convention (Meta's and the server's English strings are technical and useless to a realtor), so
 * anything English in these fields is a machine code (`WHATSAPP_NOT_CONNECTED`), developer text
 * ("Missing numberKey") or a Firebase `auth/...` code — none of which belongs on a realtor's
 * screen. They fall back to the caller's own sentence, which is always the better answer.
 *
 * Unhandled server exceptions answer `{ errors: [{ message }] }`, which the client parser leaves
 * as two nulls, so those fall back too — correctly: a stack-trace message is not customer copy.
 */
export const serverFailureMessage = (error: unknown, fallback: string): string => {
  if (!isWebotServerError(error)) {
    return fallback;
  }
  for (const value of [error.serverMessage, error.code]) {
    if (typeof value === "string" && HEBREW.test(value)) {
      return value;
    }
  }
  return fallback;
};
